// Styled in-app replacements for the browser's native alert / confirm / prompt.
// The native ones can't be themed and stamp the page origin ("localhost:5173")
// across the top; these match the app's dark dialog look (see save-model-dialog)
// and return promises. One dialog at a time is assumed (the app's usage).

const CSS = `
@keyframes dt-modal-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes dt-card-in {
  from { opacity: 0; transform: translateY(8px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
.dt-modal {
  position: fixed; inset: 0; z-index: 50; display: flex; align-items: center;
  justify-content: center; background: rgba(8,10,16,0.55);
  backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
  font: 13px/1.45 system-ui, -apple-system, sans-serif; color: #eceff6;
  animation: dt-modal-in 0.14s ease-out;
}
.dt-modal .card {
  width: 340px; max-width: calc(100vw - 32px); padding: 20px; border-radius: 16px;
  display: flex; flex-direction: column; gap: 14px;
  background: linear-gradient(180deg, rgba(36,39,50,0.98), rgba(26,28,37,0.98));
  border: 1px solid rgba(255,255,255,0.12);
  box-shadow: 0 18px 50px rgba(0,0,0,0.55), 0 1px 0 rgba(255,255,255,0.06) inset;
  animation: dt-card-in 0.18s cubic-bezier(0.16,1,0.3,1);
}
.dt-modal h3 { margin: 0; font-size: 15px; font-weight: 650; letter-spacing: 0.01em; }
.dt-modal p { margin: 0; color: #b9bfd0; white-space: pre-wrap; }
.dt-modal input {
  font: inherit; color: #eceff6; background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.14); border-radius: 8px; padding: 8px 10px;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
}
.dt-modal input:focus {
  outline: none; border-color: #6fa0ff;
  box-shadow: 0 0 0 3px rgba(111,160,255,0.25);
}
.dt-modal .row { display: flex; gap: 8px; justify-content: flex-end; margin-top: 4px; }
.dt-modal button {
  font: inherit; font-weight: 550; cursor: pointer; border-radius: 8px; padding: 7px 14px;
  border: 1px solid rgba(255,255,255,0.14); background: rgba(255,255,255,0.07);
  color: #d7dae6; transition: background 0.12s ease, border-color 0.12s ease,
  transform 0.06s ease, box-shadow 0.12s ease;
}
.dt-modal button:hover { background: rgba(255,255,255,0.13); border-color: rgba(255,255,255,0.22); }
.dt-modal button:active { transform: translateY(1px); }
.dt-modal button:focus-visible {
  outline: none; box-shadow: 0 0 0 3px rgba(111,160,255,0.4);
}
.dt-modal button.primary {
  background: #6fa0ff; color: #11141c; border-color: #6fa0ff; font-weight: 600;
}
.dt-modal button.primary:hover { background: #82b0ff; border-color: #82b0ff; }
.dt-modal button.danger { background: #c0445a; color: #fff; border-color: #c0445a; }
.dt-modal button.danger:hover { background: #d4566c; border-color: #d4566c; }
@media (prefers-reduced-motion: reduce) {
  .dt-modal, .dt-modal .card { animation: none; }
  .dt-modal { backdrop-filter: none; -webkit-backdrop-filter: none; }
}
`;

let stylesInjected = false;
function injectStyles(): void {
  if (stylesInjected) return;
  const s = document.createElement("style");
  s.textContent = CSS;
  document.head.appendChild(s);
  stylesInjected = true;
}

interface BaseOpts {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean; // style the confirm button as destructive (red)
}

type Kind = "alert" | "confirm" | "prompt";

function open(
  kind: Kind,
  message: string,
  opts: BaseOpts & { placeholder?: string; initial?: string; password?: boolean },
): Promise<unknown> {
  injectStyles();
  return new Promise((resolve) => {
    const root = document.createElement("div");
    root.className = "dt-modal";
    const card = document.createElement("div");
    card.className = "card";

    if (opts.title) {
      const h = document.createElement("h3");
      h.textContent = opts.title;
      card.appendChild(h);
    }
    if (message) {
      const p = document.createElement("p");
      p.textContent = message;
      card.appendChild(p);
    }

    let input: HTMLInputElement | null = null;
    if (kind === "prompt") {
      input = document.createElement("input");
      input.type = opts.password ? "password" : "text";
      input.value = opts.initial ?? "";
      if (opts.placeholder) input.placeholder = opts.placeholder;
      card.appendChild(input);
    }

    const row = document.createElement("div");
    row.className = "row";
    let cancelBtn: HTMLButtonElement | null = null;
    if (kind !== "alert") {
      cancelBtn = document.createElement("button");
      cancelBtn.textContent = opts.cancelLabel ?? "Cancel";
      row.appendChild(cancelBtn);
    }
    const okBtn = document.createElement("button");
    okBtn.className = opts.danger ? "danger" : "primary";
    okBtn.textContent = opts.confirmLabel ?? "OK";
    row.appendChild(okBtn);
    card.appendChild(row);
    root.appendChild(card);
    document.body.appendChild(root);

    const finish = (value: unknown): void => {
      root.remove();
      document.removeEventListener("keydown", onKey);
      resolve(value);
    };
    const cancelValue = kind === "prompt" ? null : kind === "confirm" ? false : undefined;
    const onCancel = (): void => finish(cancelValue);
    const onOk = (): void =>
      finish(kind === "prompt" ? (input as HTMLInputElement).value : kind === "confirm" ? true : undefined);

    okBtn.addEventListener("click", onOk);
    cancelBtn?.addEventListener("click", onCancel);
    root.addEventListener("mousedown", (e) => {
      if (e.target === root) onCancel(); // click the backdrop to dismiss
    });
    // Listen on document (bubble) and stop propagation so the app's global
    // keyboard shortcuts don't fire behind the modal; the input still types
    // (default action already happened by the bubble phase).
    const onKey = (e: KeyboardEvent): void => {
      e.stopPropagation();
      if (e.key === "Enter") {
        e.preventDefault();
        onOk();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
    };
    document.addEventListener("keydown", onKey);

    (input ?? okBtn).focus();
    input?.select();
  });
}

export function showAlert(
  message: string,
  opts: { title?: string; confirmLabel?: string } = {},
): Promise<void> {
  return open("alert", message, opts) as Promise<void>;
}

export function showConfirm(message: string, opts: BaseOpts = {}): Promise<boolean> {
  return open("confirm", message, opts) as Promise<boolean>;
}

// `password: true` masks the input (for credential entry — never show a
// password in a plain text field).
export function showPrompt(
  message: string,
  opts: BaseOpts & { placeholder?: string; initial?: string; password?: boolean } = {},
): Promise<string | null> {
  return open("prompt", message, opts) as Promise<string | null>;
}

// Three-button "save your work?" dialog: Don't save | Cancel | Save (primary).
export function showSaveDiscardCancel(
  message: string,
  opts: { title?: string; saveLabel?: string; discardLabel?: string } = {},
): Promise<"save" | "discard" | "cancel"> {
  injectStyles();
  return new Promise((resolve) => {
    const root = document.createElement("div");
    root.className = "dt-modal";
    const card = document.createElement("div");
    card.className = "card";
    if (opts.title) {
      const h = document.createElement("h3");
      h.textContent = opts.title;
      card.appendChild(h);
    }
    if (message) {
      const p = document.createElement("p");
      p.textContent = message;
      card.appendChild(p);
    }
    const row = document.createElement("div");
    row.className = "row";
    const discardBtn = document.createElement("button");
    discardBtn.textContent = opts.discardLabel ?? "Don't save";
    const cancelBtn = document.createElement("button");
    cancelBtn.textContent = "Cancel";
    const saveBtn = document.createElement("button");
    saveBtn.className = "primary";
    saveBtn.textContent = opts.saveLabel ?? "Save";
    row.append(discardBtn, cancelBtn, saveBtn);
    card.appendChild(row);
    root.appendChild(card);
    document.body.appendChild(root);

    const finish = (v: "save" | "discard" | "cancel"): void => {
      root.remove();
      document.removeEventListener("keydown", onKey);
      resolve(v);
    };
    discardBtn.addEventListener("click", () => finish("discard"));
    cancelBtn.addEventListener("click", () => finish("cancel"));
    saveBtn.addEventListener("click", () => finish("save"));
    root.addEventListener("mousedown", (e) => {
      if (e.target === root) finish("cancel");
    });
    const onKey = (e: KeyboardEvent): void => {
      e.stopPropagation();
      if (e.key === "Enter") {
        e.preventDefault();
        finish("save");
      } else if (e.key === "Escape") {
        e.preventDefault();
        finish("cancel");
      }
    };
    document.addEventListener("keydown", onKey);
    saveBtn.focus();
  });
}
